// Deriva os dados da aba "Desenquadramento" (Risco ANBIMA) a partir do
// walletAnalysis. A fonte autoritativa é `suitability.fracoes_risk` e a flag
// `suitability._is_valid_client_allocation_risk` — o legado `result_fracoes`
// frequentemente vem vazio pós-migração walletAnalysis e discordava do real.
//
// Espelha o padrão de getSuggestedOnlyClasses (J3): pure functions que leem do
// suitability, sem lógica reativa e sem `?.`/`??` (Babel do projeto não suporta).

import { ANBIMA_STATUS } from '@modules/consolidator/constants/anbima-status'

// Rótulo da linha agregada. Espelha OUTROS_CLASS_NAME do backend
// (aawz_base/api/db_dir/anbima/utils_anbima_adherence.py:27) — se um lado
// mudar, a linha agregada deixa de casar com a classe literal do payload.
export const OUTROS_CLASSE_LABEL = 'Outros'

const pickFracoesRisk = (walletAnalysis) => {
  const suitability = walletAnalysis && walletAnalysis.suitability
  if (!suitability) return null
  const fracoesRisk = suitability.fracoes_risk
  if (!fracoesRisk || typeof fracoesRisk !== 'object') return null
  return fracoesRisk
}

// true quando há dados de risco (fracoes_risk presente com ao menos uma classe).
export const hasRiskData = (walletAnalysis) => {
  const fracoesRisk = pickFracoesRisk(walletAnalysis)
  if (!fracoesRisk) return false
  return Object.keys(fracoesRisk).length > 0
}

// { [classeAnbima]: { fracao, signal, groupedAsOutros } } — a tabela lê
// entry.fracao + entry.signal; `groupedAsOutros=true` quando a classe NÃO tem
// teto próprio configurado e por isso está consumindo o teto de 'Outros'
// (backend soma essa classe + demais sem teto + 'Outros' literal e compara de
// uma vez contra o teto de 'Outros' — ver `grouped_as_outros` em fracoes_risk).
export const buildAnbimaFracoesFromRisk = (walletAnalysis) => {
  const fracoesRisk = pickFracoesRisk(walletAnalysis)
  if (!fracoesRisk) return {}
  const out = {}
  Object.keys(fracoesRisk).forEach((classe) => {
    const entry = fracoesRisk[classe] || {}
    const fracao = typeof entry.fracao === 'number' ? entry.fracao : null
    const signal = entry.signal || ''
    const groupedAsOutros = entry.grouped_as_outros === true
    out[classe] = { fracao, signal, groupedAsOutros }
  })
  return out
}

// { [classeAnbima]: max } — inclui apenas classes com `max` numérico (max=0 conta,
// max=null/undefined não).
export const buildAnbimaMaxFromRisk = (walletAnalysis) => {
  const fracoesRisk = pickFracoesRisk(walletAnalysis)
  if (!fracoesRisk) return {}
  const out = {}
  Object.keys(fracoesRisk).forEach((classe) => {
    const entry = fracoesRisk[classe] || {}
    if (typeof entry.max === 'number') {
      out[classe] = entry.max
    }
  })
  return out
}

// false SOMENTE quando o backend confirma explicitamente que o perfil não tem
// nenhum teto em composicao_estrategica_max_alocacao (onboarding incompleto do
// escritório). Ausência do campo (payload antigo/mock de teste) conta como
// configurado — não regride telas que não mandam esse campo ainda.
export const isAnbimaCeilingConfigured = (walletAnalysis) => {
  const suitability = walletAnalysis && walletAnalysis.suitability
  if (!suitability) return true
  return suitability.anbima_ceiling_configured !== false
}

// Cor da barra de cada linha da tabela ANBIMA. `maxPct === null` vem ANTES de
// tudo: sem teto configurado o backend devolve max=None/signal='' para toda
// classe, e cair no verde faria a tabela inteira afirmar conformidade logo
// abaixo de um cabeçalho "Não Configurado" — a mesma falsa conformidade que o
// status passou a evitar. Nesse caso a barra é neutra.
export const resolveAnbimaBarColor = (item) => {
  const safeItem = item || {}
  if (safeItem.maxPct === null || safeItem.maxPct === undefined) {
    return 'var(--gray-3)'
  }
  if (safeItem.isOverLimit) return 'var(--danger)'
  if (safeItem.isNear) return 'var(--warning-new)'
  return 'var(--success)'
}

// Soma as `fracao` das entradas agrupadas. null (em vez de 0) quando NENHUMA
// tem fração numérica — 0% e "sem dado" são coisas diferentes na tabela.
const sumPooledFracao = (pooledEntries) => {
  const temFracaoValida = pooledEntries.some((e) => typeof e.fracao === 'number')
  if (!temFracaoValida) return null
  return pooledEntries.reduce(
    (sum, e) => sum + (typeof e.fracao === 'number' ? e.fracao : 0),
    0
  )
}

// Teto compartilhado do grupo: o MENOR entre os tetos numéricos. No contrato
// atual do backend todas as entradas do pool carregam o mesmo `max` (o teto de
// 'Outros'), então o valor não muda; se um dia divergir, o menor é o único
// coerente com `resolvePooledSignal`, que devolve o sinal mais severo — pegar o
// maior teto junto com o pior sinal produzia "acima do máximo" com diff
// negativo na mesma linha.
const resolvePooledMaxPct = (pooledEntries) =>
  pooledEntries.reduce(
    (min, e) =>
      typeof e.maxPct === 'number' && (min === null || e.maxPct < min) ? e.maxPct : min,
    null
  )

const resolvePooledSignal = (pooledEntries) => {
  if (pooledEntries.some((e) => e.signal === '>')) return '>'
  if (pooledEntries.some((e) => e.signal === '~')) return '~'
  return ''
}

// Monta UMA linha da tabela de desenquadramento a partir da entrada de
// `fracoes_risk` já normalizada. Mora aqui, e não no componente, porque
// produz o MESMO shape de `buildOutrosPooledRow` (a linha agregada): as duas
// metades do contrato precisam evoluir juntas e ser testadas lado a lado.
export const buildDesenquadramentoRow = (classeAnbima, entry, maxPctRaw) => {
  const safeEntry = entry || {}
  const fracao = typeof safeEntry.fracao === 'number' ? safeEntry.fracao : null
  const maxPct = typeof maxPctRaw === 'number' ? maxPctRaw : null
  const signal = safeEntry.signal || ''
  const isOverLimit = signal === '>'
  const isNear = signal === '~'
  return {
    classe_anbima: classeAnbima,
    fracao,
    maxPct,
    signal,
    diff: fracao !== null && maxPct !== null ? fracao - maxPct : null,
    isOverLimit,
    isNear,
    barColor: resolveAnbimaBarColor({ maxPct, isOverLimit, isNear }),
    groupedAsOutros: safeEntry.groupedAsOutros === true,
  }
}

// Monta a linha agregada "Outros" a partir das classes sem teto próprio.
// O backend já soma o PL delas e compara de uma vez contra o teto de 'Outros'
// (mesmo max/signal para todas, cf. aawz_base/api/db_dir/anbima/
// utils_anbima_adherence.py:206-208) — a tabela reflete isso como UMA linha,
// com "+" detalhando o que está dentro.
export const buildOutrosPooledRow = (pooledEntries) => {
  if (!pooledEntries || !pooledEntries.length) return null
  const fracao = sumPooledFracao(pooledEntries)
  const maxPct = resolvePooledMaxPct(pooledEntries)
  const signal = resolvePooledSignal(pooledEntries)
  return {
    classe_anbima: OUTROS_CLASSE_LABEL,
    fracao,
    maxPct,
    signal,
    diff: fracao !== null && maxPct !== null ? fracao - maxPct : null,
    isOverLimit: signal === '>',
    isNear: signal === '~',
    barColor: resolveAnbimaBarColor({
      maxPct,
      isOverLimit: signal === '>',
      isNear: signal === '~',
    }),
    groupedAsOutros: true,
    isOutrosGroup: true,
    outrosBreakdown: pooledEntries
      .slice()
      .sort((a, b) => (b.fracao || 0) - (a.fracao || 0))
      .map((e) => ({ classe: e.classe_anbima, fracao: e.fracao })),
  }
}

// Status do desenquadramento. `anbima_ceiling_configured` vem primeiro: sem
// teto configurado não é "dentro do risco" por ausência de estouro, é
// "Não Configurado" — o escritório precisa configurar antes do dado ser
// confiável. Depois, `_is_valid_client_allocation_risk` é a fonte AUTORITATIVA
// para "Acima do Risco": false => acima do máximo em alguma classe. Não
// derivar só do signal, que pode não flagar classe com max=0.
export const resolveDesenquadramentoStatus = (walletAnalysis) => {
  const suitability = walletAnalysis && walletAnalysis.suitability
  if (!suitability) return null
  if (!isAnbimaCeilingConfigured(walletAnalysis)) {
    return ANBIMA_STATUS.NAO_CONFIGURADO
  }
  if (suitability._is_valid_client_allocation_risk === false) {
    return ANBIMA_STATUS.ACIMA
  }
  const fracoesRisk = pickFracoesRisk(walletAnalysis)
  if (fracoesRisk) {
    const hasProximo = Object.keys(fracoesRisk).some(function (classe) {
      const entry = fracoesRisk[classe]
      return entry && entry.signal === '~'
    })
    if (hasProximo) return ANBIMA_STATUS.PROXIMO
  }
  return ANBIMA_STATUS.DENTRO
}

// Todas as linhas da tabela: uma por classe com teto próprio, mais a linha
// agregada 'Outros' no fim quando houver classes sem teto.
//
// Sem ordenação alfabética de propósito — a tabela ordena por `fracao`
// decrescente (`:sort-by`/`:sort-desc` no v-data-table), então qualquer
// ordenação aqui seria sobrescrita.
export const buildDesenquadramentoRows = (fracoesAnbima, maxAnbimaPorClasse) => {
  const fracoes = fracoesAnbima || {}
  const maxByClasse = maxAnbimaPorClasse || {}
  const rows = []
  const pooledEntries = []
  Object.keys(fracoes).forEach((classeAnbima) => {
    const row = buildDesenquadramentoRow(
      classeAnbima,
      fracoes[classeAnbima],
      maxByClasse[classeAnbima]
    )
    if (row.groupedAsOutros) {
      pooledEntries.push(row)
    } else {
      rows.push(row)
    }
  })
  const outrosRow = buildOutrosPooledRow(pooledEntries)
  if (outrosRow) rows.push(outrosRow)
  return rows
}
